/**
 * A2UI Papan catalog — the CLIENT-CONTROLLED component set.
 *
 * The security model of A2UI hinges on this file: the agent's payload can
 * only reference component names registered here, and every renderer emits
 * plain themed React (strings render as text — React escapes; no
 * dangerouslySetInnerHTML, no URL schemes beyond https/data-image).
 * Unknown names fail soft in A2UISurface, not here.
 *
 * Keep entries QUIET: these render inside chat cards, so they reuse the
 * themed-* utility vocabulary and the FilePill visual for files — an A2UI
 * surface should look like the rest of the chat, not like a widget kit.
 */
import type { ReactNode } from 'react'
import { FilePill } from '../FilePill.js'
import { PasswordInput } from '@papan/core-ui/ui'
import { Chart as ChartHost } from './Chart.js'
import type { A2UIAction, A2UIComponent } from './types.js'

/** Render context handed to every catalog component. */
export interface A2UIRenderCtx {
  /** Binding-aware prop read: `{path}` → data-model value, else literal. */
  resolve: (v: unknown) => unknown
  /** The binding path of a prop, or null when it's a literal. */
  bindPath: (v: unknown) => string | null
  /** Write an input component's value to its bound path. */
  setData: (path: string, value: unknown) => void
  /** Fire a component action (Button etc.). */
  fire: (action: A2UIAction | undefined) => void
  /** Render `child` + `children` refs of a component, in order. */
  renderChildren: (c: A2UIComponent) => ReactNode
  /** Surface is answered / not interactive — inputs & buttons freeze. */
  disabled: boolean
  /** Open a workspace file (FileCard). */
  onOpenFile?: (path: string) => void
}

export type A2UICatalogRenderer = (c: A2UIComponent, ctx: A2UIRenderCtx) => ReactNode

const str = (v: unknown): string => (v == null ? '' : String(v))

/** Only plain web images — no papan-relative or exotic schemes for v1. */
function safeImageUrl(v: unknown): string | null {
  const s = str(v)
  if (/^https:\/\//i.test(s) || /^data:image\//i.test(s)) return s
  return null
}

/* ─── layout ─────────────────────────────────────────────────────────── */

const Card: A2UICatalogRenderer = (c, ctx) => (
  <div className="a2ui-card p-3.5 space-y-3">
    {ctx.renderChildren(c)}
  </div>
)

const GAP: Record<string, string> = { none: 'gap-0', small: 'gap-2', medium: 'gap-3', large: 'gap-5' }

const Row: A2UICatalogRenderer = (c, ctx) => (
  <div className={`flex flex-row flex-wrap items-baseline min-w-0 ${GAP[str(ctx.resolve(c.gap))] ?? 'gap-2'}`}>
    {ctx.renderChildren(c)}
  </div>
)

const Column: A2UICatalogRenderer = (c, ctx) => (
  <div className={`flex flex-col min-w-0 ${GAP[str(ctx.resolve(c.gap))] ?? 'gap-3'}`}>
    {ctx.renderChildren(c)}
  </div>
)

const Divider: A2UICatalogRenderer = () => <hr className="a2ui-divider" />

/* ─── content ────────────────────────────────────────────────────────── */

const TEXT_VARIANTS: Record<string, string> = {
  title: 'text-sm font-semibold themed-text',
  heading: 'text-xs font-semibold themed-text uppercase tracking-wider',
  body: 'text-sm themed-text',
  caption: 'text-xs themed-text-muted',
  code: 'text-xs font-mono themed-text-secondary px-1.5 py-0.5 rounded bg-black/15 w-fit',
}

const Text: A2UICatalogRenderer = (c, ctx) => (
  <span className={TEXT_VARIANTS[str(ctx.resolve(c.variant))] ?? TEXT_VARIANTS.body}>
    {str(ctx.resolve(c.text))}
  </span>
)

const Image: A2UICatalogRenderer = (c, ctx) => {
  const url = safeImageUrl(ctx.resolve(c.url))
  if (!url) return <span className="text-xs themed-text-muted italic">{str(ctx.resolve(c.alt)) || 'image unavailable'}</span>
  return <img src={url} alt={str(ctx.resolve(c.alt))} className="max-w-full max-h-60 rounded-md border themed-border-secondary" />
}

/** Apache ECharts `option` passthrough (sanitized inside ChartHost) —
 *  the schema LLMs already know; any ECharts chart type works. */
const Chart: A2UICatalogRenderer = (c, ctx) => {
  const height = Number(ctx.resolve(c.height))
  return <ChartHost option={ctx.resolve(c.option)} height={Number.isFinite(height) ? height : undefined} />
}

const FileCard: A2UICatalogRenderer = (c, ctx) => {
  const path = str(ctx.resolve(c.path))
  if (!path.startsWith('/papan/')) return null
  return <FilePill path={path} label={str(ctx.resolve(c.label)) || undefined} onClick={ctx.onOpenFile} />
}

/* ─── interactive ────────────────────────────────────────────────────── */

const BUTTON_VARIANTS: Record<string, string> = {
  primary: 'themed-btn-primary',
  secondary: 'themed-btn-secondary',
  danger: 'bg-red-600/80 hover:bg-red-600 text-white',
}

const Button: A2UICatalogRenderer = (c, ctx) => {
  // Models use `label` / `text` interchangeably; failing both, fall back
  // to a humanized event name — NEVER render an empty button.
  const label = str(ctx.resolve(c.label)) || str(ctx.resolve(c.text))
  const kids = !label && (c.child || (c.children && c.children.length > 0))
    ? ctx.renderChildren(c)
    : null
  const eventName = c.action?.event?.name
  const fallback = eventName ? eventName.charAt(0).toUpperCase() + eventName.slice(1) : 'OK'
  return (
    <button
      type="button"
      disabled={ctx.disabled}
      onClick={() => ctx.fire(c.action)}
      // self-start w-fit: flex-column parents stretch children — a lone
      // submit button must not become a full-width bar.
      className={`self-start w-fit px-3 py-1.5 rounded-md text-sm transition-colors disabled:opacity-40 disabled:cursor-default ${
        BUTTON_VARIANTS[str(ctx.resolve(c.variant))] ?? BUTTON_VARIANTS.secondary
      }`}
    >
      {label || kids || fallback}
    </button>
  )
}

function FieldLabel({ children }: { children: ReactNode }) {
  // pt-2: labeled field groups carry their own headroom on top of the
  // container gap, so sections (Environment / Release notes / …) breathe.
  return <label className="block text-xs themed-text-secondary pt-4 mb-2.5">{children}</label>
}

const TextField: A2UICatalogRenderer = (c, ctx) => {
  const { path, current } = inputBinding(c, ctx)
  const common = {
    value: str(current),
    disabled: ctx.disabled,
    placeholder: str(ctx.resolve(c.placeholder)) || undefined,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      ctx.setData(path, e.target.value)
    },
  }
  return (
    <div className="min-w-0">
      {c.label != null && <FieldLabel>{str(ctx.resolve(c.label))}</FieldLabel>}
      {str(ctx.resolve(c.type)) === 'password' ? (
        <PasswordInput {...common} className="themed-input w-full px-3 py-1.5 rounded text-sm" />
      ) : ctx.resolve(c.multiline) ? (
        <textarea {...common} rows={3} className="themed-input w-full px-3 py-1.5 rounded text-sm" />
      ) : (
        <input {...common} type="text" className="themed-input w-full px-3 py-1.5 rounded text-sm" />
      )}
    </div>
  )
}

/** Resolve an input component's WRITE path + CURRENT value.
 *  Models sometimes omit the `value: {"path": …}` binding — without a
 *  path the input would be dead (click/type does nothing). Fall back to a
 *  synthesized per-component path (`/_ui/<id>`) so the input always works
 *  and the choice still reaches the agent inside the event data. A literal
 *  `value` (e.g. a default selection) seeds the fallback read. */
function inputBinding(c: A2UIComponent, ctx: A2UIRenderCtx): { path: string; current: unknown } {
  const bound = ctx.bindPath(c.value)
  if (bound) return { path: bound, current: ctx.resolve(c.value) }
  const path = `/_ui/${c.id}`
  const written = ctx.resolve({ path })
  return { path, current: written !== undefined ? written : c.value }
}

interface OptionDef { label: string; value: string; description?: string }

function readOptions(v: unknown): OptionDef[] {
  if (!Array.isArray(v)) return []
  return v.flatMap((o): OptionDef[] => {
    if (typeof o === 'string') return [{ label: o, value: o }]
    if (o && typeof o === 'object') {
      const oo = o as Record<string, unknown>
      const value = str(oo.value ?? oo.label)
      if (!value) return []
      return [{ label: str(oo.label ?? oo.value), value, description: oo.description != null ? str(oo.description) : undefined }]
    }
    return []
  })
}

/** Shared selectable option row — themed control + hover/selected states
 *  (.a2ui-option / .a2ui-radio / .a2ui-checkbox in index.css). */
function OptionRow({ o, kind, checked, disabled, onToggle }: {
  o: OptionDef
  kind: 'radio' | 'checkbox'
  checked: boolean
  disabled: boolean
  onToggle: (checked: boolean) => void
}) {
  return (
    <label
      className="a2ui-option"
      data-selected={checked || undefined}
      data-disabled={disabled || undefined}
    >
      <input
        type={kind}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onToggle(e.target.checked)}
        className={kind === 'radio' ? 'a2ui-radio' : 'a2ui-checkbox'}
      />
      <span className="min-w-0 text-sm themed-text leading-snug">
        {o.label}
        {o.description && <span className="block text-xs themed-text-muted mt-0.5">{o.description}</span>}
      </span>
    </label>
  )
}

const RadioGroup: A2UICatalogRenderer = (c, ctx) => {
  const { path, current } = inputBinding(c, ctx)
  const selected = str(current)
  const options = readOptions(ctx.resolve(c.options))
  return (
    <div>
      {c.label != null && <FieldLabel>{str(ctx.resolve(c.label))}</FieldLabel>}
      <div className="space-y-1">
        {options.map(o => (
          <OptionRow
            key={o.value}
            o={o}
            kind="radio"
            checked={selected === o.value}
            disabled={ctx.disabled}
            onToggle={() => ctx.setData(path, o.value)}
          />
        ))}
      </div>
    </div>
  )
}

const CheckboxGroup: A2UICatalogRenderer = (c, ctx) => {
  const { path, current } = inputBinding(c, ctx)
  const selected = Array.isArray(current) ? current.map(String) : []
  const options = readOptions(ctx.resolve(c.options))
  return (
    <div>
      {c.label != null && <FieldLabel>{str(ctx.resolve(c.label))}</FieldLabel>}
      <div className="space-y-1">
        {options.map(o => (
          <OptionRow
            key={o.value}
            o={o}
            kind="checkbox"
            checked={selected.includes(o.value)}
            disabled={ctx.disabled}
            onToggle={(checked) => {
              const next = checked ? [...selected, o.value] : selected.filter(v => v !== o.value)
              ctx.setData(path, next)
            }}
          />
        ))}
      </div>
    </div>
  )
}

const Select: A2UICatalogRenderer = (c, ctx) => {
  const { path, current } = inputBinding(c, ctx)
  const selected = str(current)
  const options = readOptions(ctx.resolve(c.options))
  return (
    <div className="min-w-0">
      {c.label != null && <FieldLabel>{str(ctx.resolve(c.label))}</FieldLabel>}
      <select
        value={selected}
        disabled={ctx.disabled}
        onChange={(e) => ctx.setData(path, e.target.value)}
        className="themed-input w-full px-3 py-1.5 rounded text-sm"
      >
        {!options.some(o => o.value === selected) && <option value={selected}>{selected || 'Choose…'}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}

export const A2UI_CATALOG: Record<string, A2UICatalogRenderer> = {
  Card,
  Row,
  Column,
  Divider,
  Text,
  Image,
  FileCard,
  Chart,
  Button,
  TextField,
  RadioGroup,
  CheckboxGroup,
  Select,
}
